import { Stack } from 'common/ui/Layout';
import { useCriticalResults } from 'domains/worklist/Worklist/hooks/useCriticalResults';
import { CriticalResultStatus } from 'generated/graphql';
import { useCurrentCase } from 'hooks/useCurrentCase';
import { useCallback, useMemo, useState } from 'react';
import Text from 'common/ui/Text';
import { Colors } from 'styles';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { useRecorderVoiceCommandEventsListener } from '../hooks/useRecorderVoiceCommandEventsListener';
import Tooltip from 'common/ui/Tooltip';
import { trackWorklistAction } from 'domains/worklist/analytics/useWorklistAnalytics';
import { worklist } from 'modules/analytics/constants';
import { telemetryContextFor } from 'modules/Apollo/types';
import { FlagCriticalResultModal } from 'common/CriticalResults/FlagCriticalResultModal';
import { rootTheme } from 'theme/rootTheme';

export const TOOLTIP_DELAY = 500;

export const CriticalResultsToggle = ({ isDisabled = false }: { isDisabled?: boolean }) => {
  const { currentCase } = useCurrentCase();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [entrypoint, setEntrypoint] = useState<Nullable<string>>(null);

  const {
    hasAccessToCriticalWorkflow,
    changeCriticalResultStatus,
    isChangeCriticalResultStatusLoading,
  } = useCriticalResults({ context: APOLLO_QUERY_CONTEXT });

  const hasCriticalResult = useMemo(() => {
    return currentCase?.criticalResultStatus === CriticalResultStatus.Critical;
  }, [currentCase?.criticalResultStatus]);

  const toggleCriticalResultStatus = useCallback(() => {
    if (!currentCase?.smid) {
      return;
    }

    const newStatus = hasCriticalResult
      ? CriticalResultStatus.NotCritical
      : CriticalResultStatus.Critical;

    if (newStatus === CriticalResultStatus.NotCritical) {
      trackWorklistAction(worklist.usr.changeCriticalResult, {
        entrypoint: 'toggle',
        oldStatus: currentCase?.criticalResultStatus,
        newStatus,
        worklistItemSmid: currentCase?.smid,
      });

      changeCriticalResultStatus(currentCase?.smid, newStatus);
    } else {
      setIsModalOpen(true);
      setEntrypoint('toggle');
    }
  }, [
    changeCriticalResultStatus,
    currentCase?.criticalResultStatus,
    currentCase?.smid,
    hasCriticalResult,
  ]);

  const shouldDisableToggle = useMemo(() => {
    return isDisabled || isChangeCriticalResultStatusLoading;
  }, [isChangeCriticalResultStatusLoading, isDisabled]);

  useRecorderVoiceCommandEventsListener(({ source, payload }) => {
    if (shouldDisableToggle) {
      return;
    }

    if (!currentCase?.smid) {
      return;
    }

    switch (payload.substitution.action) {
      case 'ClearCriticalResult':
        trackWorklistAction(worklist.usr.changeCriticalResult, {
          entrypoint: 'voice_command',
          oldStatus: currentCase?.criticalResultStatus,
          newStatus: CriticalResultStatus.NotCritical,
          worklistItemSmid: currentCase?.smid,
        });

        changeCriticalResultStatus(currentCase.smid, CriticalResultStatus.NotCritical);
        break;
      case 'FlagCriticalResult':
        setIsModalOpen(true);
        setEntrypoint('voice_command');
        break;
      default:
        break;
    }
  });

  if (!hasAccessToCriticalWorkflow) {
    return null;
  }

  return (
    <>
      <FlagCriticalResultModal
        worklistItemSmid={currentCase?.smid ?? ''}
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        onSubmit={async () => {
          if (currentCase != null) {
            changeCriticalResultStatus(currentCase?.smid, CriticalResultStatus.Critical);
            trackWorklistAction(worklist.usr.changeCriticalResult, {
              entrypoint,
              oldStatus: currentCase?.criticalResultStatus,
              newStatus: CriticalResultStatus.Critical,
              worklistItemSmid: currentCase?.smid,
            });
            setEntrypoint(null);
          }
        }}
      />
      <Tooltip
        delay={[TOOLTIP_DELAY, 0]}
        content={
          <Text
            variant="body2"
            css={`
              color: ${Colors.gray1};
            `}
          >
            Say "<b>{hasCriticalResult ? 'Clear' : 'Flag'} critical result"</b>
          </Text>
        }
        backgroundColor={Colors.gray8}
      >
        <Stack
          alignY="center"
          alignX="center"
          css={`
            padding: 2px 8px 2px 4px;
            border-radius: ${rootTheme.radius.sm}px;
            opacity: ${shouldDisableToggle ? 0.5 : 1};
            border: 1px solid ${hasCriticalResult ? '#EE5F55' : 'transparent'};
            background-color: ${hasCriticalResult ? 'rgba(238, 95, 85, 0.16)' : 'transparent'};
            cursor: pointer;
          `}
        >
          <FormControlLabel
            disabled={shouldDisableToggle}
            labelPlacement="start"
            label={
              <Text
                data-testid="critical-result-toggle-label"
                css={hasCriticalResult ? `color: ${Colors.red7}; font-weight: 500;` : ''}
              >
                Critical Result
              </Text>
            }
            control={
              <Switch
                disabled={shouldDisableToggle}
                size="small"
                data-testid="critical-result-toggle"
                checked={hasCriticalResult}
                onChange={toggleCriticalResultStatus}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: Colors.red6,
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: Colors.red4,
                    opacity: 1,
                  },
                }}
              />
            }
            onClick={(e) => e.stopPropagation()}
          />
        </Stack>
      </Tooltip>
    </>
  );
};

CriticalResultsToggle.displayName = 'CriticalResultsToggle';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(CriticalResultsToggle);
